import { api } from './client';
import type { Paged } from './admin';

export type BlogLanguage = 'Bg' | 'En';
export type BlogPostStatus = 'Draft' | 'Scheduled' | 'Published' | 'Archived' | 'NeedsAttention';
export type BlogPostOrigin = 'Manual' | 'Ai';

export interface BlogFaqItem {
  question: string;
  answer: string;
}

export interface BlogSource {
  title: string;
  url: string;
}

export interface BlogPostListItem {
  id: string;
  title: string;
  slug: string;
  language: BlogLanguage;
  status: BlogPostStatus;
  origin: BlogPostOrigin;
  publishAt: string | null;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string | null;
  reviewScore: number | null;
  parameterYear: number | null;
  wordCount: number;
}

export interface BlogPost {
  id: string;
  translationGroupId: string;
  language: BlogLanguage;
  slug: string;
  title: string;
  metaTitle: string | null;
  metaDescription: string | null;
  excerpt: string;
  bodyHtml: string;
  coverImageKey: string | null;
  coverImageUrl: string | null;
  coverImageAlt: string | null;
  ogImageUrl: string | null;
  tags: string[];
  faq: BlogFaqItem[];
  sources: BlogSource[];
  socialTeaser: string | null;
  status: BlogPostStatus;
  origin: BlogPostOrigin;
  publishAt: string | null;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string | null;
  reviewScore: number | null;
  reviewJson: string | null;
  parameterYear: number | null;
  readingMinutes: number;
  wordCount: number;
  noIndex: boolean;
  translationId: string | null;
  /** Absolute. The console runs on its own host and cannot derive the public site's address. */
  publicUrl: string;
}

/** What the editor sends. Blank slug and excerpt let the server derive them. */
export interface SaveBlogPost {
  language: BlogLanguage;
  title: string;
  slug: string | null;
  metaTitle: string | null;
  metaDescription: string | null;
  excerpt: string | null;
  bodyHtml: string;
  coverImageKey: string | null;
  coverImageAlt: string | null;
  tags: string[];
  faq: BlogFaqItem[];
  sources: BlogSource[];
  socialTeaser: string | null;
  noIndex: boolean;
  translationOfId: string | null;
}

export interface BlogTag {
  id: string;
  slug: string;
  nameBg: string;
  nameEn: string;
  postCount: number;
}

export interface MediaAsset {
  id: string;
  key: string;
  url: string;
  contentType: string;
  bytes: number;
  width: number;
  height: number;
  alt: string | null;
  createdAt: string;
}

export interface SlugAvailability {
  available: boolean;
  suggestion: string;
}

export interface PreviewLink {
  url: string;
  expiresAt: string;
}

const base = '/api/admin/blog';
const lang = (language: BlogLanguage) => (language === 'En' ? 'en' : 'bg');

export const blogApi = {
  posts: (filter: { status?: BlogPostStatus | ''; language?: BlogLanguage | ''; search?: string; page: number }) => {
    const query = new URLSearchParams({ page: String(filter.page), pageSize: '25' });
    if (filter.status) query.set('status', filter.status);
    if (filter.language) query.set('lang', lang(filter.language));
    if (filter.search) query.set('search', filter.search);
    return api<Paged<BlogPostListItem>>(`${base}/posts?${query}`);
  },

  post: (id: string) => api<BlogPost>(`${base}/posts/${id}`),

  create: (post: SaveBlogPost) => api<BlogPost>(`${base}/posts`, { method: 'POST', body: JSON.stringify(post) }),

  update: (id: string, post: SaveBlogPost) =>
    api<BlogPost>(`${base}/posts/${id}`, { method: 'PUT', body: JSON.stringify(post) }),

  /** `publishAt` null publishes now; a future ISO instant schedules. */
  publish: (id: string, publishAt: string | null) =>
    api<BlogPost>(`${base}/posts/${id}/publish`, { method: 'POST', body: JSON.stringify({ publishAt }) }),

  unpublish: (id: string) => api<BlogPost>(`${base}/posts/${id}/unpublish`, { method: 'POST' }),

  archive: (id: string) => api<BlogPost>(`${base}/posts/${id}/archive`, { method: 'POST' }),

  remove: (id: string) => api<void>(`${base}/posts/${id}`, { method: 'DELETE' }),

  previewLink: (id: string) => api<PreviewLink>(`${base}/posts/${id}/preview-link`, { method: 'POST' }),

  slugAvailable: (language: BlogLanguage, slug: string, exceptId?: string) => {
    const query = new URLSearchParams({ lang: lang(language), slug });
    if (exceptId) query.set('exceptId', exceptId);
    return api<SlugAvailability>(`${base}/slug-available?${query}`);
  },

  tags: () => api<BlogTag[]>(`${base}/tags`),

  createTag: (tag: { slug: string; nameBg: string; nameEn: string }) =>
    api<BlogTag>(`${base}/tags`, { method: 'POST', body: JSON.stringify(tag) }),

  updateTag: (id: string, tag: { slug: string; nameBg: string; nameEn: string }) =>
    api<BlogTag>(`${base}/tags/${id}`, { method: 'PUT', body: JSON.stringify(tag) }),

  removeTag: (id: string) => api<void>(`${base}/tags/${id}`, { method: 'DELETE' }),

  media: (page: number) => api<Paged<MediaAsset>>(`${base}/media?page=${page}&pageSize=40`),

  /** Multipart. The client leaves the content type alone for a FormData body — see client.ts. */
  upload: (file: File | Blob, alt?: string) => {
    const form = new FormData();
    form.append('file', file, file instanceof File ? file.name : 'pasted-image');
    if (alt) form.append('alt', alt);
    return api<MediaAsset>(`${base}/media`, { method: 'POST', body: form });
  },

  removeMedia: (id: string) => api<void>(`${base}/media/${id}`, { method: 'DELETE' }),
};

// ---------------------------------------------------------------------------------------------
// The generator: topics, runs and the automation switch
// ---------------------------------------------------------------------------------------------

export type BlogTopicStatus = 'Queued' | 'InProgress' | 'Used' | 'Rejected';
export type BlogRunStatus = 'Running' | 'Published' | 'Drafted' | 'NeedsAttention' | 'Failed' | 'Skipped';
export type BlogRunTrigger = 'Auto' | 'Manual' | 'Planner' | 'PersonaRefresh' | 'Translation';

export interface BlogTopic {
  id: string;
  key: string;
  title: string;
  brief: string;
  targetKeywords: string[];
  category: string;
  priority: number;
  preferredMonths: number[];
  status: BlogTopicStatus;
  source: 'Seeded' | 'Planner' | 'Manual';
  usedByPostId: string | null;
  usedAt: string | null;
  lastError: string | null;
  createdAt: string;
}

export interface SaveBlogTopic {
  key: string | null;
  title: string;
  brief: string;
  targetKeywords: string[];
  category: string;
  priority: number;
  preferredMonths: number[];
}

export interface BlogAutomationStatus {
  configured: boolean;
  enabled: boolean;
  paused: boolean;
  pausedAt: string | null;
  pauseNote: string | null;
  model: string;
  promptVersion: string;
  publishHourSofia: number;
  monthKey: string;
  monthSpendUsd: number;
  monthlyBudgetUsd: number;
  queuedTopics: number;
  minTopicBacklog: number;
  needsAttentionPosts: number;
  /** Every reason today's automatic article would not be written right now. Empty means it will be. */
  blockers: string[];
}

export interface BlogRun {
  id: string;
  trigger: BlogRunTrigger;
  status: BlogRunStatus;
  /** What a running run is doing: research, writing, checking, reviewing, revising, saving. */
  stage: string | null;
  scheduledForDate: string | null;
  attempt: number;
  language: BlogLanguage;
  topicId: string | null;
  topicTitle: string | null;
  postId: string | null;
  postTitle: string | null;
  model: string;
  promptVersion: string;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  estimatedCostUsd: number;
  durationMs: number;
  reviewScore: number | null;
  error: string | null;
  startedAt: string;
  finishedAt: string | null;
}

export interface BlogRunDetail {
  run: BlogRun;
  researchBrief: string | null;
  writerOutputJson: string | null;
  reviewJson: string | null;
}

/** What is stored on a generated post and on its run: the model's review and the machine's findings. */
export interface StoredReview {
  score: number | null;
  verdict: string | null;
  checks: { id: string; pass: boolean; note: string }[];
  issues: { severity: 'blocker' | 'major' | 'minor'; quote: string; problem: string; fix: string }[];
  deterministic: {
    wordCount: number;
    ungroundedFigures: { token: string; kind: string; context: string }[];
    /** Citations of a provision that appears in neither the grounding pack nor the research notes. */
    ungroundedReferences?: string[];
    advicePhrases: string[];
    droppedSources: string[];
    unlinkedTargets: string[];
    unknownTags: string[];
    similarTitle: string | null;
    blockers: string[];
  };
  revised: boolean;
  /** What the first draft was sent back for. Absent on runs from before this was recorded. */
  revisionReasons?: string[] | null;
}

export function parseReview(json: string | null): StoredReview | null {
  if (!json) return null;

  try {
    return JSON.parse(json) as StoredReview;
  } catch {
    return null;
  }
}

export const generationApi = {
  status: () => api<BlogAutomationStatus>(`${base}/automation`),

  pause: (note: string | null) =>
    api<BlogAutomationStatus>(`${base}/automation/pause`, { method: 'POST', body: JSON.stringify({ note }) }),

  resume: () => api<BlogAutomationStatus>(`${base}/automation/resume`, { method: 'POST' }),

  /** Answers at once with the run's row; the article takes a few minutes and the run is polled. */
  generate: (topicId: string | null) =>
    api<BlogRun>(`${base}/automation/generate`, { method: 'POST', body: JSON.stringify({ topicId }) }),

  plan: () => api<BlogRun>(`${base}/automation/plan`, { method: 'POST' }),

  translate: (postId: string) => api<BlogRun>(`${base}/posts/${postId}/translate`, { method: 'POST' }),

  runs: (page: number) => api<Paged<BlogRun>>(`${base}/runs?page=${page}&pageSize=25`),

  run: (id: string) => api<BlogRunDetail>(`${base}/runs/${id}`),

  topics: (status: BlogTopicStatus | '') => api<BlogTopic[]>(`${base}/topics${status ? `?status=${status}` : ''}`),

  createTopic: (topic: SaveBlogTopic) => api<BlogTopic>(`${base}/topics`, { method: 'POST', body: JSON.stringify(topic) }),

  updateTopic: (id: string, topic: SaveBlogTopic) =>
    api<BlogTopic>(`${base}/topics/${id}`, { method: 'PUT', body: JSON.stringify(topic) }),

  rejectTopic: (id: string) => api<void>(`${base}/topics/${id}/reject`, { method: 'POST' }),

  requeueTopic: (id: string) => api<void>(`${base}/topics/${id}/requeue`, { method: 'POST' }),

  removeTopic: (id: string) => api<void>(`${base}/topics/${id}`, { method: 'DELETE' }),
};
